import { HttpError, IResourceComponentsProps, useList } from "@refinedev/core";
import { MediaUploadSection, MediaItem } from "@/components/MediaUploadSection";
import { Create, useForm, useSelect } from "@refinedev/antd";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Button,
  Space,
  Switch,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { apiUrlV2 } from "@/consts";
import { TOKEN_KEY } from "@/authProvider";

// Initialize dayjs duration plugin
dayjs.extend(duration);

interface MissionType {
  id: string | number;
  value: string;
  format: "TARGET_BASED" | "ROUND_BASED";
}

export const MissionCreate: React.FC<IResourceComponentsProps> = () => {
  const { formProps, saveButtonProps, onFinish } = useForm<any, HttpError, any>({
    resource: "mission",
    successNotification: (data, values) => {
        return {
          message: data?.data.message,
          type: "success",
        };
      },
      errorNotification: (data, values) => {
        return {
          message: data?.response?.data,
          type: "error",
        };
      },
      action: "create",
    }
  );

  const [attachments, setAttachments] = useState<MediaItem[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadingFileName(file.name);
    setUploadError(null);
    // Show pending in attachments
    setAttachments((prev: any[]) => [
      ...prev,
      { id: "pending", type: file.type, name: file.name, pending: true },
    ]);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const body = {
        fileCount: 1,
        mimeType: file.type.startsWith("image") ? "IMAGE" : "VIDEO",
        uploadType: "MISSION",
      };
      const res = await fetch(`${apiUrlV2}/file/presigned_url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const result = await res.json();
      const fileData = result?.data?.[0];
      if (!fileData) throw new Error("No file data returned");
      // S3 POST upload
      const formData = new FormData();
      Object.entries(fileData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append("Content-Type", file.type);
      formData.append("file", file);
      await fetch(fileData.url, {
        method: "POST",
        body: formData,
      });
      // Remove pending and add real item
      setAttachments((prev: any[]) => [
        ...prev.filter((m) => !(m.pending && m.name === file.name)),
        { id: fileData.fileID, type: file.type, name: file.name },
      ]);
    } catch (err) {
      setUploadError("Upload failed");
      // Remove pending item on error
      setAttachments((prev: any[]) =>
        prev.filter((m) => !(m.pending && m.name === file.name))
      );
    }
    setUploading(false);
    setUploadingFileName(null);
  };

  const {
    selectProps: missionTypeProps,
    query: { isLoading: missionTypeLoading },
  } = useSelect({
    resource: "mission_type/for_select_list",
    optionLabel: "value",
    optionValue: "slug",
  });

  const {
    selectProps: targetUnitProps,
    query: { isLoading: targetUnitLoading },
  } = useSelect({
    resource: "target_unit/for_select_list",
    optionLabel: "value",
    optionValue: "slug",
  });

  const {
    selectProps: workoutSelectProps,
    query: { isLoading: workoutsLoading },
  } = useSelect({
    resource: "workout/for_select_list",
    optionLabel: "value",
    optionValue: "id",
  });

  formProps.onFinish = async (values) => {
    const submitValues = { ...values };
   
    if (attachments.length > 0) {
      submitValues.fileIDs = attachments.filter((a) => !a.pending).map((a) => a.id);
    }


    if (submitValues.hasDuration && submitValues.durationLimitMinutes) {
      const { amount, unit } = submitValues.durationLimitMinutes;
      let minutes = amount;
      switch (unit) {
        case "minutes":
          break;
        case "hours":
          minutes *= 60;
          break;
        case "days":
          minutes *= 1440;
          break;
        case "weeks":
          minutes *= 10080;
          break;
      }
      submitValues.durationLimitMinutes = minutes;
    } else {
      delete submitValues.durationLimitMinutes;
    }
    delete submitValues.hasDuration; 

    if (submitValues.targetValue && submitValues.targetUnitSlug) {
      submitValues.missionTarget = {
        targetValue: submitValues.targetValue,
        targetUnitSlug: submitValues.targetUnitSlug,
        ...(submitValues.mode && { missionMode: submitValues.mode }),
      };
      delete submitValues.targetValue;
      delete submitValues.targetUnitSlug;
      if (submitValues.mode) {
        delete submitValues.mode;
      }
    }

    if (submitValues.targetValue) {
      submitValues.missionTarget = {
        targetValue: submitValues.targetValue,
        targetUnitSlug: "CALORIES",
      };
      delete submitValues.targetValue;
    }

    // Transform the workoutsSets into missionRound format for ROUND_BASED type
    if (submitValues.workoutsSets && submitValues.workoutsSets.length > 0) {
      submitValues.missionRound = {
        rounds: submitValues.rounds,
        workouts: submitValues.workoutsSets[0].workouts || [],
      };
      delete submitValues.workoutsSets;
      delete submitValues.rounds;
    }

    if (submitValues.timeLimitMinutes) {
      const { amount, unit } = submitValues.timeLimitMinutes;
      let minutes = amount;
      switch (unit) {
        case "minutes":
          break;
        case "hours":
          minutes *= 60;
          break;
        case "days":
          minutes *= 1440;
          break;
        case "weeks":
          minutes *= 10080;
          break;
      }
      submitValues.timeLimitMinutes = minutes;
    }

    try {
      await formProps.form!.validateFields();
    } catch (error) {
      console.error("Validation error:", error);
    }

    return onFinish(submitValues);
  };

  // Fix SaveButton disabled prop type issue
  const mergedSaveButtonProps = { ...saveButtonProps, disabled: uploading };

  return (
    <Create saveButtonProps={mergedSaveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item label="Featured" name={["isFeatured"]} valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Media">
          {attachments.length > 0 ? (
            <MediaUploadSection
              initialMedia={attachments}
              onChange={setAttachments}
            />
          ) : (
            <div>
              <Button
                type="primary"
                disabled={uploading}
                onClick={() =>
                  document.getElementById("media-upload-input")?.click()
                }
              >
                Upload Media
              </Button>
              <input
                id="media-upload-input"
                type="file"
                accept="image/*,video/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                  e.target.value = "";
                }}
              />
              {uploadError && (
                <div style={{ color: "red", marginTop: 8 }}>{uploadError}</div>
              )}
            </div>
          )}
        </Form.Item>
        <Form.Item
          label="Name"
          name={["name"]}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Description" name={["description"]}>
          <Input />
        </Form.Item>

        <Form.Item
          label="Type"
          name={["missionTypeSlug"]}
          rules={[{ required: true }]}
        >
          <Select {...missionTypeProps} loading={missionTypeLoading} />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues?.missionTypeSlug !== currentValues?.missionTypeSlug
          }
        >
          {({ getFieldValue }) => {
            const missionType = missionTypeProps?.options?.find(
              (option: any) => option.value === getFieldValue("missionTypeSlug")
            );
            const label = missionType?.label;

            if (label === "Health Stats") {
              return (
                <>
                  <Form.Item label="Target" required>
                    <Input.Group compact>
                      <Form.Item
                        name={["targetValue"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                            type: "number",
                            min: 0,
                            message: "Please enter a positive number",
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          step="1"
                          style={{ width: "50%" }}
                          placeholder="Enter target value"
                        />
                      </Form.Item>
                      <Form.Item
                        name={["targetUnitSlug"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                          },
                        ]}
                      >
                        <Select
                          {...targetUnitProps}
                          loading={targetUnitLoading}
                          style={{ width: "50%" }}
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                  <div style={{ marginTop: 24 }} />
                  <Form.Item
                    label="Mode"
                    name={["mode"]}
                    rules={[
                      { required: true, message: "Please select a mode" },
                    ]}
                  >
                    <Select
                      options={[
                        { label: "Daily Stat", value: "DAILY_STAT" },
                        {
                          label: "Activity Session",
                          value: "ACTIVITY_SESSION",
                        },
                      ]}
                      placeholder="Select mode"
                    />
                  </Form.Item>
                </>
              );
            } else if (label === "First to Finish") {
              return (
                <>
                  <Form.Item
                    label="Rounds"
                    name="rounds"
                    rules={[{ required: true, message: "Please enter rounds" }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>

                  <Form.Item label="Time limit">
                    <Input.Group compact>
                      <Form.Item
                        name={["timeLimitMinutes", "amount"]}
                        noStyle
                        rules={[
                          { required: true, message: "Time limit is required" },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "50%" }}
                          min={1}
                          placeholder="Amount"
                        />
                      </Form.Item>
                      <Form.Item
                        name={["timeLimitMinutes", "unit"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                            message: "Time limit unit is required",
                          },
                        ]}
                      >
                        <Select
                          style={{ width: "50%" }}
                          placeholder="Unit"
                          options={[
                            { label: "Minutes", value: "minutes" },
                            { label: "Hours", value: "hours" },
                            { label: "Days", value: "days" },
                            { label: "Weeks", value: "weeks" },
                          ]}
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>

                  <Form.List
                    name={["workoutsSets", 0, "workouts"]}
                    rules={[
                      {
                        validator: async (_, workouts) => {
                          if (!workouts || workouts.length < 1) {
                            return Promise.reject(
                              new Error("At least one workout is required")
                            );
                          }
                        },
                      },
                    ]}
                  >
                    {(
                      workoutFields,
                      { add: addWorkout, remove: removeWorkout }
                    ) => (
                      <>
                        {workoutFields.map(({ key, name, ...restField }) => (
                          <Space
                            key={key}
                            style={{ display: "flex", marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              {...restField}
                              name={[name, "workoutID"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a workout",
                                },
                              ]}
                            >
                              <Select
                                {...workoutSelectProps}
                                loading={workoutsLoading}
                                style={{ width: "200px" }}
                                placeholder="Select workout"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "reps"]}
                              rules={[
                                { required: true, message: "Missing reps" },
                              ]}
                            >
                              <InputNumber
                                placeholder="Reps"
                                min={1}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => removeWorkout(name)}
                            />
                          </Space>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => addWorkout()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Workout
                          </Button>
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            block
                            icon={<PlusOutlined />}
                            onClick={() =>
                              document.getElementById("media-upload")?.click()
                            }
                          >
                            Upload Media
                          </Button>
                          <input
                            id="media-upload"
                            type="file"
                            accept="image/*,video/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              // Handle file upload logic here
                              const file = e.target.files?.[0];
                              if (file) {
                                // TODO: Add upload logic
                                console.log("Selected media:", file);
                              }
                            }}
                          />
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              );
            } else if (label === "Calories") {
              return (
                <>
                  <Form.Item
                    label="Rounds"
                    name="rounds"
                    rules={[{ required: true, message: "Please enter rounds" }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>
                  <Form.List
                    name={["workoutsSets", 0, "workouts"]}
                    rules={[
                      {
                        validator: async (_, workouts) => {
                          if (!workouts || workouts.length < 1) {
                            return Promise.reject(
                              new Error("At least one workout is required")
                            );
                          }
                        },
                      },
                    ]}
                  >
                    {(
                      workoutFields,
                      { add: addWorkout, remove: removeWorkout }
                    ) => (
                      <>
                        {workoutFields.map(({ key, name, ...restField }) => (
                          <Space
                            key={key}
                            style={{ display: "flex", marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              {...restField}
                              name={[name, "workoutID"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a workout",
                                },
                              ]}
                            >
                              <Select
                                {...workoutSelectProps}
                                style={{ width: "200px" }}
                                placeholder="Select workout"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "reps"]}
                              rules={[
                                { required: true, message: "Missing reps" },
                              ]}
                            >
                              <InputNumber
                                placeholder="Reps"
                                min={1}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => removeWorkout(name)}
                            />
                          </Space>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => addWorkout()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Workout
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                  <Form.Item label="Target calories" required>
                    <Input.Group compact>
                      <Form.Item
                        name={["targetValue"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                            type: "number",
                            min: 0,
                            message: "Please enter a positive number",
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          step="1"
                          style={{ width: "50%" }}
                          placeholder="Enter target value"
                        />
                      </Form.Item>
                      <Form.Item name={["targetUnitSlug"]} noStyle>
                        <Input
                          readOnly
                          value="CALORIES"
                          aria-label="Calories"
                          placeholder="Calories"
                          style={{ width: "10%" }}
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </>
              );
            } else if (label === "Rounds" || label === "Workouts") {
              return (
                <>
                  <Form.Item
                    label="Rounds"
                    name="rounds"
                    rules={[{ required: true, message: "Please enter rounds" }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>

                  <Form.List
                    name={["workoutsSets", 0, "workouts"]}
                    rules={[
                      {
                        validator: async (_, workouts) => {
                          if (!workouts || workouts.length < 1) {
                            return Promise.reject(
                              new Error("At least one workout is required")
                            );
                          }
                        },
                      },
                    ]}
                  >
                    {(
                      workoutFields,
                      { add: addWorkout, remove: removeWorkout }
                    ) => (
                      <>
                        {workoutFields.map(({ key, name, ...restField }) => (
                          <Space
                            key={key}
                            style={{ display: "flex", marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              {...restField}
                              name={[name, "workoutID"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a workout",
                                },
                              ]}
                            >
                              <Select
                                {...workoutSelectProps}
                                style={{ width: "200px" }}
                                placeholder="Select workout"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "reps"]}
                              rules={[
                                { required: true, message: "Missing reps" },
                              ]}
                            >
                              <InputNumber
                                placeholder="Reps"
                                min={1}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                            <MinusCircleOutlined
                              onClick={() => removeWorkout(name)}
                            />
                          </Space>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => addWorkout()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Workout
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              );
            }

            return null;
          }}
        </Form.Item>

        {/* Ends At field always at the very end, after all conditional fields */}
        <Form.Item
          label="Ends At"
          name={["endsAt"]}
          rules={[{ required: true, message: "Please select an end date/time" }]}
        >
          <DatePicker showTime style={{ width: "100%" }} placement="bottomLeft" />
        </Form.Item>
      </Form>
    </Create>
  );
};
